import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Tag } from './ui'
import { useT } from '../lib/i18n'
import type { CaseEntry } from '../lib/locales'

interface CasesPageProps {
  onOpenPlaybook: () => void
}

const splitTag = (tag: string): string[] =>
  tag.split('·').map((s) => s.trim()).filter((s) => s.length > 0)

export function CasesPage({ onOpenPlaybook }: CasesPageProps) {
  return (
    <main>
      <CasesHero />
      <CasesListWithFilter />
      <PlaybookCTA onOpenPlaybook={onOpenPlaybook} />
    </main>
  )
}

function CasesHero() {
  const h = useT().cases.hero
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 pt-20 sm:pt-28 pb-10 sm:pb-14">
      <Tag kind="rule" className="text-[length:var(--text-micro)] mb-5">
        {h.tag}
      </Tag>
      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-[color:var(--color-orion-text)] leading-[1.05] max-w-4xl"
        style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
      >
        {h.title}
      </motion.h1>
      <p className="mt-4 mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
        {h.subtitle}
      </p>
      <p
        className="mt-6 max-w-3xl text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
      >
        {h.leadParagraph}
      </p>
    </section>
  )
}

function CasesListWithFilter() {
  const cases = useT().cases.cases
  const f = useT().cases.filters

  // Derive unique industry / function tags from all cases.
  const allIndustries = useMemo(
    () => Array.from(new Set(cases.flatMap((c) => splitTag(c.industryTag)))).sort(),
    [cases],
  )
  const allFunctions = useMemo(
    () => Array.from(new Set(cases.flatMap((c) => splitTag(c.functionTag)))).sort(),
    [cases],
  )

  const [selectedIndustries, setSelectedIndustries] = useState<Set<string>>(new Set())
  const [selectedFunctions, setSelectedFunctions] = useState<Set<string>>(new Set())

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      const caseInd = splitTag(c.industryTag)
      const caseFunc = splitTag(c.functionTag)
      const indOk =
        selectedIndustries.size === 0 || caseInd.some((i) => selectedIndustries.has(i))
      const funcOk =
        selectedFunctions.size === 0 || caseFunc.some((fn) => selectedFunctions.has(fn))
      return indOk && funcOk
    })
  }, [cases, selectedIndustries, selectedFunctions])

  // Cascading counts: for each industry chip, count cases that include that
  // industry AND match the CURRENT function filter (and vice versa). This
  // lets the two filter rows respond to each other so the user can see
  // which combinations actually have cases.
  const industryCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const ind of allIndustries) {
      const n = cases.filter((c) => {
        const caseInd = splitTag(c.industryTag)
        const caseFunc = splitTag(c.functionTag)
        const hasInd = caseInd.includes(ind)
        const funcOk =
          selectedFunctions.size === 0 || caseFunc.some((fn) => selectedFunctions.has(fn))
        return hasInd && funcOk
      }).length
      map.set(ind, n)
    }
    return map
  }, [allIndustries, cases, selectedFunctions])

  const functionCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const fn of allFunctions) {
      const n = cases.filter((c) => {
        const caseInd = splitTag(c.industryTag)
        const caseFunc = splitTag(c.functionTag)
        const hasFn = caseFunc.includes(fn)
        const indOk =
          selectedIndustries.size === 0 || caseInd.some((i) => selectedIndustries.has(i))
        return hasFn && indOk
      }).length
      map.set(fn, n)
    }
    return map
  }, [allFunctions, cases, selectedIndustries])

  const toggle = (set: Set<string>, value: string): Set<string> => {
    const next = new Set(set)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    return next
  }

  const clearAll = () => {
    setSelectedIndustries(new Set())
    setSelectedFunctions(new Set())
  }

  const totalSelected = selectedIndustries.size + selectedFunctions.size
  const resultsCount = f.resultsCountTpl
    .replace('{filtered}', String(filteredCases.length))
    .replace('{total}', String(cases.length))

  return (
    <>
      {/* Filter section */}
      <section className="mx-auto max-w-6xl px-6 sm:px-10 pb-6 sm:pb-8">
        <div className="surface-night border-faint rounded-[var(--radius-lg)] p-5 sm:p-6 space-y-4">
          <FilterRow
            label={f.industryLabel}
            options={allIndustries}
            counts={industryCounts}
            selected={selectedIndustries}
            onToggle={(v) => setSelectedIndustries((prev) => toggle(prev, v))}
          />
          <FilterRow
            label={f.functionLabel}
            options={allFunctions}
            counts={functionCounts}
            selected={selectedFunctions}
            onToggle={(v) => setSelectedFunctions((prev) => toggle(prev, v))}
          />
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[color:var(--color-orion-surface)]/30">
            <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
              {resultsCount}
            </div>
            {totalSelected > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] hover:text-[color:var(--color-orion-text)] transition-colors"
              >
                {f.clearLabel} ✕
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Case list */}
      <section className="mx-auto max-w-6xl px-6 sm:px-10 pb-10 sm:pb-14 space-y-10">
        {filteredCases.length === 0 ? (
          <EmptyState />
        ) : (
          filteredCases.map((c, idx) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.02, 0.15) }}
            >
              <CaseCard entry={c} index={idx} />
            </motion.div>
          ))
        )}
      </section>
    </>
  )
}

function FilterRow({
  label,
  options,
  counts,
  selected,
  onToggle,
}: {
  label: string
  options: string[]
  counts: Map<string, number>
  selected: Set<string>
  onToggle: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
      <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] shrink-0 min-w-[64px]">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = selected.has(opt)
          const count = counts.get(opt) ?? 0
          const empty = count === 0 && !active
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              aria-pressed={active}
              className={
                'mono text-[length:var(--text-micro)] px-2.5 py-1 rounded-[var(--radius-xs)] transition-all duration-150 inline-flex items-baseline gap-1.5 ' +
                (active
                  ? 'bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium'
                  : empty
                    ? 'border-faint text-[color:var(--color-orion-haze)] opacity-40 hover:opacity-70'
                    : 'border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:border-[color:var(--color-orion-amber)]/40')
              }
            >
              <span>{opt}</span>
              <span
                className={
                  'text-[10px] opacity-70 ' +
                  (active ? 'text-[color:var(--color-orion-deep)]' : '')
                }
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function EmptyState() {
  const f = useT().cases.filters
  return (
    <div className="surface-night border-faint rounded-[var(--radius-lg)] p-12 text-center">
      <div className="text-[color:var(--color-orion-text)] mb-2" style={{ fontSize: 'var(--text-h3)', fontWeight: 500 }}>
        {f.emptyStateTitle}
      </div>
      <p className="text-[color:var(--color-orion-haze)]">{f.emptyStateBody}</p>
    </div>
  )
}

function CaseCard({ entry, index }: { entry: CaseEntry; index: number }) {
  const labels = useT().cases.sectionLabels
  return (
    <article
      id={entry.id}
      className="surface-night border-faint rounded-[var(--radius-xl)] p-6 sm:p-8 scroll-mt-24"
    >
      {/* Header row */}
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] uppercase tracking-widest mr-1">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-text)] px-2 py-0.5 rounded-[var(--radius-xs)] border-faint bg-[color:var(--color-orion-deep)]/40 uppercase tracking-widest">
            {entry.industryTag}
          </span>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] px-2 py-0.5 rounded-[var(--radius-xs)] border-faint">
            {entry.functionTag}
          </span>
        </div>
        <h2
          className="text-[color:var(--color-orion-text)] leading-snug font-medium mb-3"
          style={{ fontSize: 'var(--text-h2)' }}
        >
          {entry.title}
        </h2>
        {/* Customer chip — highlighted */}
        <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-[var(--radius-sm)] border border-[color:var(--color-orion-amber)]/40 bg-[color:var(--color-orion-amber)]/5">
          <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
            {labels.customer}
          </span>
          <span className="text-[color:var(--color-orion-text)] text-[length:var(--text-small)] font-medium">
            {entry.customer}
          </span>
        </div>
      </header>

      {/* Two-column: Pain + Solution */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-3">
            {labels.painPoints}
          </div>
          <ul className="space-y-2.5">
            {entry.painPoints.map((p, i) => (
              <li
                key={i}
                className="flex items-baseline gap-2.5 text-[color:var(--color-orion-text)] text-[length:var(--text-small)] leading-relaxed"
              >
                <span className="mono text-[color:var(--color-orion-haze)] shrink-0 opacity-70">·</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
            {labels.solution}
          </div>
          <ul className="space-y-2.5">
            {entry.solution.map((s, i) => (
              <li
                key={i}
                className="flex items-baseline gap-2.5 text-[color:var(--color-orion-text)] text-[length:var(--text-small)] leading-relaxed"
              >
                <span className="mono text-[color:var(--color-orion-amber)] shrink-0">→</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Outcomes — proper table */}
      <div className="mb-5">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
          {labels.outcomes}
        </div>
        <div className="overflow-x-auto border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-md)] bg-[color:var(--color-orion-deep)]/30">
          <table className="w-full text-[length:var(--text-small)]">
            <thead>
              <tr className="border-b border-[color:var(--color-orion-surface)]/40">
                <th className="text-left px-4 py-2.5 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] w-[40%]">
                  {labels.metricHeader}
                </th>
                <th className="text-left px-4 py-2.5 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] w-[30%]">
                  {labels.beforeHeader}
                </th>
                <th className="text-left px-4 py-2.5 mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] w-[30%]">
                  {labels.afterHeader}
                </th>
              </tr>
            </thead>
            <tbody>
              {entry.metrics.map((m, i) => (
                <tr
                  key={i}
                  className="border-b border-[color:var(--color-orion-surface)]/20 last:border-b-0"
                >
                  <td className="px-4 py-2.5 text-[color:var(--color-orion-text)] font-medium">
                    {m.label}
                  </td>
                  <td className="px-4 py-2.5 text-[color:var(--color-orion-haze)] mono opacity-80">
                    {m.before}
                  </td>
                  <td className="px-4 py-2.5 text-[color:var(--color-orion-amber)] mono font-medium">
                    {m.after}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insight */}
      <p
        className="text-[color:var(--color-orion-text)] leading-relaxed italic mb-5"
        style={{ fontSize: 'var(--text-lead)' }}
      >
        &ldquo;{entry.insight}&rdquo;
      </p>

      {/* Paradigm tags */}
      <div className="flex flex-wrap gap-1.5 pt-4 border-t border-[color:var(--color-orion-surface)]/30">
        <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mr-2">
          {labels.paradigms}
        </span>
        {entry.paradigmTags.map((p, i) => (
          <span
            key={i}
            className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] px-2 py-0.5 rounded-[var(--radius-xs)] border-faint"
          >
            {p}
          </span>
        ))}
      </div>
    </article>
  )
}

function PlaybookCTA({ onOpenPlaybook }: { onOpenPlaybook: () => void }) {
  const c = useT().cases.playbookCta
  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16 sm:py-20">
      <button
        type="button"
        onClick={onOpenPlaybook}
        className="w-full text-left surface-night border-faint border-l-2 border-l-[color:var(--color-orion-amber)] rounded-[var(--radius-lg)] p-6 sm:p-8 hover:border-[color:var(--color-orion-amber)]/60 transition-colors group"
      >
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
          {c.tag}
        </div>
        <div className="text-[color:var(--color-orion-text)] font-medium mb-2" style={{ fontSize: 'var(--text-h3)' }}>
          {c.title}
        </div>
        <p className="text-[color:var(--color-orion-haze)] leading-relaxed mb-3 max-w-3xl">{c.body}</p>
        <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] group-hover:text-[color:var(--color-orion-text)] transition-colors">
          {c.cta}
        </div>
      </button>
    </section>
  )
}
